import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StorageService } from '../../core/services/storage.service';
import { BlockUiService } from '../services/blockUi/block-ui.service';
import { ReloginModalComponent } from './relogin-modal.component';

@Injectable({
  providedIn: 'root',
})
export class ReloginModalService {
  // Self-defending against stacked modals: HttpResponseInterceptor already gates
  // concurrent 401s with isAwaitingReAuth, but that guard lives in the interceptor,
  // not here — any other future caller of open() should still only ever get one
  // modal at a time, so this service caches the in-flight result itself too.
  private pendingModal: Promise<void> | null = null;

  constructor(
    private readonly modalService: NgbModal,
    private readonly storageService: StorageService,
    private readonly blockUiService: BlockUiService,
  ) {}

  // Resolves once the user has re-authenticated; rejects on cancel (or if they
  // need 2FA, which this modal doesn't handle inline — see ReloginModalComponent).
  open(): Promise<void> {
    if (this.pendingModal) {
      return this.pendingModal;
    }

    // The request that triggered this is usually still "in flight" from the caller's
    // point of view (e.g. campaign-form's saveDraft() only closes BlockUiService in a
    // finalize() that won't fire until re-auth + replay finish) — so its global
    // "Processing..." overlay would otherwise sit on top of this modal for as long as
    // the user takes to type their password. Drop it for the modal's lifetime instead.
    this.blockUiService.close();

    const modalRef = this.modalService.open(ReloginModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      windowClass: 'relogin-modal-window',
    });

    modalRef.componentInstance.email = this.storageService.email;

    this.pendingModal = modalRef.result.finally(() => {
      this.pendingModal = null;
    });

    return this.pendingModal;
  }
}
